import React, { createContext, useContext, useReducer, useEffect } from "react";
import { supabase } from "../lib/supabase";
const AppContext = createContext();

const initialState = {
  user: {
    name: "",
    age: "",
    timezone: "UTC",
    preferredTwinType: "sibling",
    interests: [],
    sleepSchedule: { bedtime: "22:00", wakeup: "07:00" },
    reminderSettings: {
      hydration: true,
      sleep: true,
      selfCare: true,
      checkIn: true,
    },
    joinDate: new Date().toISOString(),
    credits: 0,
  },
  twin: {
    id: "",
    name: "",
    personality: "caring",
    relationship: "sibling",
    affectionLevel: 50,
    voiceId: "default",
    memories: [],
    conversationHistory: [],
  },
  mood: {
    current: "neutral",
    history: [],
    lastCheckIn: null,
  },
  isOnboarded: false,
  currentMood: "neutral",
  chatMessages: [],
  isVoiceActive: false,
  reminders: [],
  mode: "companion", // "companion" or "partner"
  rogueModeActive: false,
  notifications: [
    { id: 1, title: "System Ready", message: "System is fully synchronized.", type: "success", time: "Just now" },
    { id: 2, title: "Memory Sync", message: "Successfully indexed last conversation.", type: "info", time: "2m ago" }
  ],
};

const APP_CACHE_KEY = "twinHeartData:v2";
const cacheableState = (state = {}) => ({
  user: state.user ? {
    ...initialState.user,
    name: state.user.name || "",
    age: state.user.age || "",
    timezone: state.user.timezone || "UTC",
    preferredTwinType: state.user.preferredTwinType || "sibling",
    interests: (state.user.interests || []).slice(0, 20),
    sleepSchedule: state.user.sleepSchedule || initialState.user.sleepSchedule,
    reminderSettings: state.user.reminderSettings || initialState.user.reminderSettings,
    joinDate: state.user.joinDate || initialState.user.joinDate,
    credits: Number(state.user.credits) || 0,
  } : initialState.user,
  twin: state.twin ? { ...initialState.twin, id: state.twin.id || "", name: state.twin.name || "", personality: state.twin.personality || "caring", relationship: state.twin.relationship || "sibling", affectionLevel: state.twin.affectionLevel || 50, voiceId: state.twin.voiceId || "default", memories: [], conversationHistory: [] } : initialState.twin,
  mood: { current: state.mood?.current || "neutral", history: [], lastCheckIn: state.mood?.lastCheckIn || null },
  isOnboarded: Boolean(state.isOnboarded),
  currentMood: state.currentMood || "neutral",
  isVoiceActive: false,
  mode: state.mode || "companion",
  rogueModeActive: Boolean(state.rogueModeActive),
  chatMessages: [],
  reminders: [],
  notifications: [],
});

function appReducer(state, action) {
  switch (action.type) {
    case "SET_USER_DATA":
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case "SET_TWIN_DATA":
      return {
        ...state,
        twin: { ...state.twin, ...action.payload },
      };
    case "ADD_CHAT_MESSAGE":
      return {
        ...state,
        chatMessages: [...state.chatMessages, action.payload].slice(-200),
      };
    case "ADD_CHAT_MESSAGES":
      return {
        ...state,
        chatMessages: [...state.chatMessages, ...action.payload].slice(-200),
      };
    case "SET_CHAT_MESSAGES":
      return {
        ...state,
        chatMessages: action.payload.slice(-200),
      };
    case "ADD_NOTIFICATION":
      return {
        ...state,
        notifications: [{ id: Date.now(), ...action.payload }, ...state.notifications].slice(0, 50),
      };
    case "CLEAR_NOTIFICATIONS":
      return {
        ...state,
        notifications: [],
      };
    case "SET_MOOD":
      const newMoodEntry = {
        mood: action.payload,
        timestamp: new Date().toISOString(),
        date: new Date().toDateString(),
      };
      return {
        ...state,
        currentMood: action.payload,
        mood: {
          ...state.mood,
          current: action.payload,
          history: [...state.mood.history, newMoodEntry].slice(-100),
          lastCheckIn: new Date().toISOString(),
        },
      };
    case "COMPLETE_ONBOARDING":
      return {
        ...state,
        isOnboarded: true,
      };
    case "TOGGLE_VOICE":
      return {
        ...state,
        isVoiceActive: !state.isVoiceActive,
      };
    case "TOGGLE_ROGUE_MODE":
      return {
        ...state,
        rogueModeActive: !state.rogueModeActive,
      };
    case "ADD_MEMORY":
      return {
        ...state,
        twin: {
          ...state.twin,
          memories: [...state.twin.memories, action.payload].slice(-100),
        },
      };
    case "ADD_REMINDER":
      return {
        ...state,
        reminders: [...state.reminders, action.payload].slice(-100),
      };
    case "SET_MODE":
      return {
        ...state,
        mode: action.payload,
      };
    case "LOAD_STATE":
      return {
        ...state,
        ...action.payload,
      };
    case "RESET_STATE":
      return initialState;
    default:
      return state;
  }
}

function loadInitialState(defaultState) {
  try {
    const savedData = localStorage.getItem(APP_CACHE_KEY) || localStorage.getItem("twinHeartData");
    return savedData ? { ...defaultState, ...cacheableState(JSON.parse(savedData)) } : defaultState;
  } catch (error) {
    console.error("Error loading data:", error);
    return defaultState;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState, loadInitialState);

  // Apply rogue-mode class to html element when active
  useEffect(() => {
    if (state.rogueModeActive) {
      document.documentElement.classList.add("rogue-mode");
    } else {
      document.documentElement.classList.remove("rogue-mode");
    }
  }, [state.rogueModeActive]);

  // Batch synchronous storage writes so rapid chat/mood updates do not block
  // rendering multiple times in the same interaction.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      try {
        localStorage.setItem(APP_CACHE_KEY, JSON.stringify(cacheableState(state)));
        localStorage.removeItem("twinHeartData");
      } catch (error) {
        console.error("Error saving data:", error);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [state]);
  // const fetchInitialData = useCallback(async () => {
  //   const userId = (await supabase.auth.getUser())?.data?.user?.id;
  //   if (!userId) return;

  //   const { data: userData } = await supabase
  //     .from("users")
  //     .select("*")
  //     .eq("id", userId)
  //     .single();
  //   const { data: twinData } = await supabase
  //     .from("twins")
  //     .select("*")
  //     .eq("user_id", userId)
  //     .single();
  //   const { data: moodData } = await supabase
  //     .from("moods")
  //     .select("*")
  //     .eq("user_id", userId)
  //     .single();
  //   const { data: chatData } = await supabase
  //     .from("chat_messages")
  //     .select("*")
  //     .eq("user_id", userId);
  //   const { data: reminders } = await supabase
  //     .from("reminders")
  //     .select("*")
  //     .eq("user_id", userId);
  //   const { data: settings } = await supabase
  //     .from("settings")
  //     .select("*")
  //     .eq("user_id", userId)
  //     .single();

  //   dispatch({
  //     type: "LOAD_STATE",
  //     payload: {
  //       user: userData,
  //       twin: twinData,
  //       mood: moodData,
  //       chatMessages: chatData?.map((c) => c.message) || [],
  //       reminders,
  //       isOnboarded: settings?.is_onboarded ?? false,
  //       isVoiceActive: settings?.is_voice_active ?? false,
  //     },
  //   });
  // }, []);

  // const persistState = useCallback(async () => {
  //   const userId = (await supabase.auth.getUser())?.data?.user?.id;
  //   if (!userId) return;

  //   await supabase.from("users").upsert({ id: userId, ...state.user });
  //   await supabase.from("twins").upsert({ user_id: userId, ...state.twin });
  //   await supabase.from("moods").upsert({ user_id: userId, ...state.mood });
  //   await supabase.from("settings").upsert({
  //     user_id: userId,
  //     is_onboarded: state.isOnboarded,
  //     is_voice_active: state.isVoiceActive,
  //   });

  //   if (state.chatMessages.length > 0) {
  //     const chatUpserts = state.chatMessages.map((msg) => ({
  //       user_id: userId,
  //       message: msg,
  //       created_at: new Date().toISOString(),
  //     }));
  //     await supabase.from("chat_messages").upsert(chatUpserts);
  //   }

  //   if (state.reminders.length > 0) {
  //     const reminderUpserts = state.reminders.map((r) => ({
  //       user_id: userId,
  //       ...r,
  //     }));
  //     await supabase.from("reminders").upsert(reminderUpserts);
  //   }
  // }, [state]);

  // useEffect(() => {
  //   fetchInitialData();
  // }, [fetchInitialData]);

  // useEffect(() => {
  //   persistState();
  // }, [state, persistState]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}
